import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Observable } from 'rxjs';
import * as exago from '../../feature-modules/exago/models/exago.models';
import { map } from 'rxjs/operators';
import { builderSessionConfig, reportSessionConfig } from '../configurations/exago-session.config';

@Injectable({
  providedIn: 'root',
})
export class ExagoService {
  //Linux static exagoBatchUrl: string = 'ExagoWebApi/rest/batch';
  static exagoBatchUrl: string = 'ExagoWebAPI/rest/Batch';

  constructor(private readonly httpClient: HttpClient) {}

  initReportSession(
    role: string,
    exagoQuery: exago.IExagoQuery,
    ReportPath: string,
    type: exago.EWrReportType,
  ): Observable<exago.IExagoBatchResponse[]> {
    const sessionConfig: exago.IExagoSessionRequest = {
      ...reportSessionConfig,
      ApiAction:
        type === exago.EWrReportType.ExpressView ? exago.EWrApiAction.EditReport : exago.EWrApiAction.ExecuteReport,
      ReportSettings: { ReportPath },
    };
    return this.initSession(role, exagoQuery, sessionConfig);
  }

  initBuilderSession(role: string, exagoQuery: exago.IExagoQuery): Observable<exago.IExagoBatchResponse[]> {
    return this.initSession(role, exagoQuery, builderSessionConfig);
  }

  private initSession(
    role: string,
    data: exago.IExagoQuery,
    sessionConfig: exago.IExagoSessionRequest,
  ): Observable<exago.IExagoBatchResponse[]> {
    return this.httpClient.post<exago.IExagoBatchResponse[]>(
      `${environment.reporting_api_url}/${ExagoService.exagoBatchUrl}`,
      [
        this.getSessionPayload(sessionConfig),
        this.getStorageManagementPayload(data.userId),
        this.getRolePayload(role),
        this.getParameterPayload(exago.EExagoQueryKeys.SiteIds, data.siteIds),
        this.getParameterPayload(exago.EExagoQueryKeys.AccountIds, data.accountIds),
        this.getParameterPayload(exago.EExagoQueryKeys.StartDate, data.startDate),
        this.getParameterPayload(exago.EExagoQueryKeys.EndDate, data.endDate),
        this.getParameterPayload(exago.EExagoQueryKeys.ReportStartDate, data.reportStartDate),
        this.getParameterPayload(exago.EExagoQueryKeys.ReportEndDate, data.reportEndDate),
        this.getParameterPayload(exago.EExagoQueryKeys.ReportAccount, data.accounts),
        this.getParameterPayload(exago.EExagoQueryKeys.ReportSite, data.siteNames),
        this.getParameterPayload(exago.EExagoQueryKeys.Month1, data.month1),
        this.getParameterPayload(exago.EExagoQueryKeys.Month2, data.month2),
        this.getParameterPayload(exago.EExagoQueryKeys.M2Formatted, data.m2Formatted),
        this.getParameterPayload(exago.EExagoQueryKeys.M1Formatted, data.m1Formatted),
        this.getParameterPayload(exago.EExagoQueryKeys.Scorecard, data.scorecardIds),
      ],
    );
  }

  getFolders(userId: number, role: string): Observable<exago.IExagoFolders> {
    const reportsListApiResponseIndex = 3;
    return this.httpClient
      .post<
        exago.IExagoBatchResponse[]
      >(`${environment.reporting_api_url}/${ExagoService.exagoBatchUrl}`, [this.getExagoBatchRequest(exago.EExagoBatchUrlType.Sessions, exago.EExagoBatchMethodType.POST), this.getStorageManagementPayload(userId), this.getRolePayload(role), this.getExagoBatchRequest(`${exago.EExagoBatchUrlType.Reports}/${exago.EExagoBatchUrlType.List}`, exago.EExagoBatchMethodType.GET)])
      .pipe(map((res) => res[reportsListApiResponseIndex].ResponseContent as exago.IExagoFolders));
  }

  // https://exagobi.com/support/administrators/rest-web-service-api/sessions/
  private getSessionPayload(sessionConfig: exago.IExagoSessionRequest): exago.IExagoBatchRequest {
    return this.getExagoBatchRequest<exago.IExagoSessionRequest>(
      exago.EExagoBatchUrlType.Sessions,
      exago.EExagoBatchMethodType.POST,
      sessionConfig,
    );
  }

  // https://exagobi.com/support/administrators/rest-web-service-api/storage-management-1/
  private getStorageManagementPayload(userId: number): exago.IExagoBatchRequest {
    return this.getExagoBatchRequest(`${exago.EExagoBatchUrlType.StorageMgmt}`, exago.EExagoBatchMethodType.PATCH, {
      userId,
    });
  }

  // https://exagobi.com/support/administrators/rest-web-service-api/role-security/
  private getRolePayload(role: string): exago.IExagoBatchRequest {
    return this.getExagoBatchRequest(`${exago.EExagoBatchUrlType.Roles}/${role}`, exago.EExagoBatchMethodType.PATCH, {
      isActive: true,
    });
  }

  // https://exagobi.com/support/administrators/rest-web-service-api/parameters-3/
  private getParameterPayload(name: string, value: string | number): exago.IExagoBatchRequest {
    return this.getExagoBatchRequest(
      `${exago.EExagoBatchUrlType.Parameters}/${name}`,
      exago.EExagoBatchMethodType.PATCH,
      { Value: value },
    );
  }

  private getExagoBatchRequest<T = unknown>(
    Url: string,
    Method: exago.EExagoBatchMethodType,
    Payload?: T,
  ): exago.IExagoBatchRequest<T> {
    let request = { Url, Method };

    if (Payload) {
      request = { ...request, Payload } as exago.IExagoBatchRequest;
    }

    return request;
  }
}
